import '@/components/Faqs/Tree/tree.css';

import { ActionIcon, Box, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconChevronRight, IconGripVertical, IconX } from '@tabler/icons-react';
import Tree from 'rc-tree';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { faqsChildNoe, FAQTreeItem, I18N_LANG_MAP, DatasetsInfoWithProgress } from '@mygpt/app';

import { DatasetSelect } from '@/components/Common/DatasetSelect';
import { MyTreeNode } from '@/components/Faqs/Tree';
import { NodeItem } from '@/components/Widget/Embed/form-context';

// 可排序的右侧列表项组件
const SortableItem = ({
  node,
  onRemove,
  datasets,
}: {
  node: NodeItem;
  onRemove: (id: string) => void;
  datasets: DatasetsInfoWithProgress[];
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 获取节点所属的知识库名称
  const datasetName = node.dataset_id ? datasets.find((d) => d.id === node.dataset_id)?.name : undefined;

  return (
    <Paper ref={setNodeRef} style={style} p={6} withBorder className="bg-white hover:bg-gray-50">
      <Group wrap="nowrap" gap={6}>
        <ActionIcon
          {...attributes}
          {...listeners}
          variant="subtle"
          size="xs"
          className="cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <IconGripVertical size={12} />
        </ActionIcon>

        <Box className="flex-1 min-w-0 overflow-hidden">
          <Text size="xs" className="truncate" lineClamp={1} title={node.question}>
            {node.question}
          </Text>
          {datasetName && (
            <Text
              size="xs"
              c="dimmed"
              className="truncate"
              lineClamp={1}
              style={{ fontSize: '10px', lineHeight: '14px' }}
              title={datasetName}
            >
              {datasetName}
            </Text>
          )}
        </Box>

        <ActionIcon size="xs" variant="subtle" color="red" className="flex-shrink-0" onClick={() => onRemove(node.id)}>
          <IconX size={12} />
        </ActionIcon>
      </Group>
    </Paper>
  );
};

// 左侧树节点组件
const TreeNodeItem = ({
  node,
  selectedNodeIds,
  onAddNode,
}: {
  node: MyTreeNode;
  selectedNodeIds: string[];
  onAddNode: (node: NodeItem) => void;
}) => {
  const isSelected = selectedNodeIds.includes(node.key);

  return (
    <Box
      className={`transition-colors ${isSelected ? 'text-gray-500 cursor-not-allowed' : 'cursor-pointer'}`}
      p={4}
      onClick={() => {
        if (!isSelected) {
          onAddNode({
            id: node.key,
            question: node.title,
          });
        }
      }}
    >
      <Group wrap="nowrap" gap={4}>
        <Text size="xs" className="flex-1 truncate min-w-0" lineClamp={1}>
          {node.title}
        </Text>
        {!isSelected && (
          <ActionIcon size="xs" variant="subtle" className="flex-shrink-0">
            <IconChevronRight />
          </ActionIcon>
        )}
      </Group>
    </Box>
  );
};

interface QuestionTreeTransferProps {
  datasets: DatasetsInfoWithProgress[];
  selectedDatasetId: string;
  selectedNodes: NodeItem[];
  onDatasetChange: (datasetId: string) => void;
  onNodesChange: (nodes: NodeItem[]) => void;
  disabled?: boolean;
  onTreeSetup?: (datasetId: string) => void;
}

export const QuestionTreeTransfer = ({
  datasets,
  selectedDatasetId,
  selectedNodes,
  onDatasetChange,
  onNodesChange,
  disabled = false,
  onTreeSetup,
}: QuestionTreeTransferProps) => {
  const { t, i18n } = useTranslation();
  const [treeData, setTreeData] = useState<MyTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // 获取问答树数据
  const fetchTreeData = useCallback(
    async (datasetId: string) => {
      if (!datasetId) return;

      setLoading(true);
      try {
        const params = {
          datasetId,
          hierarchy_parent_id: '0',
          num_levels: 0,
          language: I18N_LANG_MAP[i18n.language],
        };
        const { data: resData } = await faqsChildNoe(params);

        const loop = (data: FAQTreeItem[]): MyTreeNode[] => {
          return data.map((item) => ({
            key: item.id,
            title: item.question,
            customData: item,
            children: item.child_nodes.length ? loop(item.child_nodes) : [],
          }));
        };

        // 检查是否为导入进度响应
        if (resData && 'status' in resData && resData.status === 'importing') {
          // 如果正在导入中，显示空树
          setTreeData([]);
        } else {
          // 正常的FAQ树数据
          const treeData = resData && Array.isArray(resData) && resData.length ? loop(resData) : [];
          setTreeData(treeData);
        }
      } catch (error) {
        console.error('Failed to fetch tree data:', error);
        setTreeData([]);
      } finally {
        setLoading(false);
      }
    },
    [i18n.language]
  );

  // 当知识库选择改变时，重新加载树数据
  useEffect(() => {
    if (selectedDatasetId) {
      fetchTreeData(selectedDatasetId);
    } else {
      setTreeData([]);
    }
  }, [selectedDatasetId, fetchTreeData]);

  // 处理拖拽开始
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // 处理拖拽结束
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = selectedNodes.findIndex((node) => node.id === active.id);
        const newIndex = selectedNodes.findIndex((node) => node.id === over.id);

        const newNodes = arrayMove(selectedNodes, oldIndex, newIndex);
        onNodesChange(newNodes);
      }

      setActiveId(null);
    },
    [selectedNodes, onNodesChange]
  );

  // 添加节点到右侧
  const handleAddNode = useCallback(
    (node: NodeItem) => {
      if (selectedNodes.some((n) => n.id === node.id)) return;
      // 添加节点时，包含当前选择的知识库ID信息
      const nodeWithDataset = {
        ...node,
        dataset_id: selectedDatasetId,
      };
      onNodesChange([...selectedNodes, nodeWithDataset]);
    },
    [selectedNodes, selectedDatasetId, onNodesChange]
  );

  // 从右侧移除节点
  const handleRemoveNode = useCallback(
    (nodeId: string) => {
      onNodesChange(selectedNodes.filter((node) => node.id !== nodeId));
    },
    [selectedNodes, onNodesChange]
  );

  // 获取当前拖拽的节点
  const activeNode = selectedNodes.find((node) => node.id === activeId);
  const selectedNodeIds = useMemo(() => selectedNodes.map((node) => node.id), [selectedNodes]);

  return (
    <Stack gap="md" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      {/* 知识库选择 */}
      <DatasetSelect
        datasets={datasets}
        value={selectedDatasetId}
        onChange={(value) => value && onDatasetChange(value)}
        disabled={disabled}
        size="sm"
      />

      {selectedDatasetId && (
        <Group align="flex-start" gap="sm" wrap="nowrap" className="w-full">
          {/* 左侧面板 - 可选问答树 */}
          <Box className="w-1/2">
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500}>
                {t('widget.setting.chatbot.available_questions')}
              </Text>
            </Group>

            <Paper withBorder p="xs" style={{ height: '280px' }}>
              <div style={{ height: '260px', overflowY: 'auto', marginRight: '-4px' }}>
                {loading ? (
                  <Group justify="center" p="md">
                    <Loader size="xs" />
                  </Group>
                ) : treeData.length > 0 ? (
                  <Tree
                    treeData={treeData}
                    titleRender={(node: MyTreeNode) => (
                      <TreeNodeItem node={node} selectedNodeIds={selectedNodeIds} onAddNode={handleAddNode} />
                    )}
                    showLine
                  />
                ) : (
                  <Stack gap={4} className="p-2">
                    <Text size="xs" c="dimmed" ta="center">
                      {t('widget.setting.chatbot.no_tree_tip')}
                    </Text>
                    <Text
                      size="xs"
                      c="violet.8"
                      className="cursor-pointer text-center"
                      onClick={() => {
                        // 打开问答树设置界面
                        if (onTreeSetup) {
                          onTreeSetup(selectedDatasetId);
                        }
                      }}
                    >
                      {t('widget.setting.chatbot.no_tree_set')}
                    </Text>
                  </Stack>
                )}
              </div>
            </Paper>
          </Box>

          {/* 右侧面板 - 已选问答树 */}
          <Box className="w-1/2">
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500}>
                {t('widget.setting.chatbot.selected_questions')}
              </Text>
            </Group>

            <Paper withBorder p="xs" style={{ height: '280px', overflow: 'hidden' }}>
              <div style={{ height: '260px', overflowY: 'auto', paddingRight: '4px', marginRight: '-4px' }}>
                {selectedNodes.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={selectedNodes.map((node) => node.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Stack gap="xs">
                        {selectedNodes.map((node) => (
                          <SortableItem key={node.id} node={node} onRemove={handleRemoveNode} datasets={datasets} />
                        ))}
                      </Stack>
                    </SortableContext>

                    <DragOverlay>
                      {activeNode ? (
                        <Paper p="xs" withBorder className="bg-white shadow-lg">
                          <Text size="xs" truncate>
                            {activeNode.question}
                          </Text>
                        </Paper>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                ) : (
                  <Text size="xs" c="dimmed" ta="center" p="md">
                    {t('widget.setting.chatbot.no_questions_selected')}
                  </Text>
                )}
              </div>
            </Paper>
          </Box>
        </Group>
      )}
    </Stack>
  );
};
